import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext, useSearchParams } from 'react-router-dom';
import {
  IonButton,
  IonSpinner,
  IonItem,
  IonLabel,
  IonCheckbox
} from '@ionic/react';
import {
  logoGithub,
  sparklesOutline
} from 'ionicons/icons';
import { CURSOR_MODELS, CLAUDE_MODELS, CODEX_MODELS, GEMINI_MODELS, getDefaultModelForAgent } from '../../constants/aiModels';
import CollapsibleCard from '../../components/atoms/CollapsibleCard';
import api from '../../services/api';
import './ProjectSettings.css';

const gitlabLogoSvg = 'data:image/svg+xml,' + encodeURIComponent('<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M23.955 13.587l-1.342-4.135-2.664-8.189a.455.455 0 00-.867 0L16.418 9.45H7.582L4.918 1.263a.455.455 0 00-.867 0L1.386 9.452.045 13.587a.924.924 0 00.331 1.023L12 23.054l11.624-8.443a.92.92 0 00.331-1.024" fill="#E24329"/><path d="M12 23.054L16.418 9.45H7.582L12 23.054z" fill="#FC6D26"/><path d="M12 23.054l-4.418-13.6H1.386L12 23.054z" fill="#FCA326"/><path d="M1.386 9.452L.045 13.587a.924.924 0 00.331 1.023L12 23.054 1.386 9.452z" fill="#E24329"/><path d="M12 23.054l4.418-13.6h6.196L12 23.054z" fill="#FCA326"/><path d="M22.614 9.452l1.341 4.135a.924.924 0 01-.331 1.023L12 23.054l10.614-13.602z" fill="#E24329"/></svg>');

const ProjectSettings = () => {
  const { projectId } = useParams();
  const { project, setToast } = useOutletContext();
  const [searchParams] = useSearchParams();
  const expandSection = searchParams.get('section');
  
  const [saving, setSaving] = useState(false);
  const [githubRepos, setGithubRepos] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [hasLoadedRepos, setHasLoadedRepos] = useState(false);
  
  const [gitlabRepos, setGitlabRepos] = useState([]);
  const [loadingGitlabRepos, setLoadingGitlabRepos] = useState(false);
  const [hasLoadedGitlabRepos, setHasLoadedGitlabRepos] = useState(false);
  
  const [selectedRepos, setSelectedRepos] = useState([]);
  const [selectedGitlabRepos, setSelectedGitlabRepos] = useState([]);
  
  const [aiAgent, setAiAgent] = useState('cursor');
  const [aiModel, setAiModel] = useState('auto');
  const [customApiKey, setCustomApiKey] = useState('');
  // Claude only — separate field for Pro/Max subscription OAuth token
  // (sk-ant-oat01-…). User can fill EITHER api key OR oauth; runtime
  // prefers oauth when both present.
  const [customOAuthToken, setCustomOAuthToken] = useState('');
  
  const [secrets] = useState({
    npmToken: '',
    gemToken: '',
    mavenUsername: '',
    mavenPassword: '',
    pipToken: ''
  });
  
  useEffect(() => {
    if (project) {
      if (project.githubRepos && Array.isArray(project.githubRepos)) {
        setSelectedRepos(project.githubRepos);
      } else if (project.githubRepo) {
        setSelectedRepos([project.githubRepo]);
      } else {
        setSelectedRepos([]);
      }
      
      if (project.gitlabRepos && Array.isArray(project.gitlabRepos)) {
        setSelectedGitlabRepos(project.gitlabRepos);
      } else {
        setSelectedGitlabRepos([]);
      }
      
      setAiAgent(project.aiAgent || 'cursor');
      setAiModel(project.aiModel || 'auto');
      
      if (project.secrets?.aiApiKey && typeof project.secrets.aiApiKey === 'string') {
        setCustomApiKey(project.secrets.aiApiKey);
      } else {
        setCustomApiKey('');
      }
      if (project.secrets?.aiOAuthToken && typeof project.secrets.aiOAuthToken === 'string') {
        setCustomOAuthToken(project.secrets.aiOAuthToken);
      } else {
        setCustomOAuthToken('');
      }
    }
  }, [project, projectId]);

  const loadGithubRepos = async () => {
    if (hasLoadedRepos) return;
    
    setLoadingRepos(true);
    try {
      const data = await api.get('/github/repos');
      console.log('📦 GitHub repos loaded:', data.repos?.length || 0, 'repos');
      setGithubRepos(data.repos || []);
      setHasLoadedRepos(true);
      
      if (data.needsReconnect) {
        setToast({
          show: true,
          message: data.message || 'Only public repositories visible. Reconnect GitHub to access private repos.',
          color: 'warning',
          duration: 8000
        });
      } else if (data.message) {
        setToast({
          show: true,
          message: data.message,
          color: 'warning'
        });
      }
    } catch (error) {
      console.error('❌ Error loading GitHub repos:', error);
      setToast({
        show: true,
        message: 'Failed to load GitHub repositories. Please try again.',
        color: 'danger'
      });
    } finally {
      setLoadingRepos(false);
    }
  };

  const handleRepoToggle = (repoFullName) => {
    setSelectedRepos(prev => {
      if (prev.includes(repoFullName)) {
        return prev.filter(r => r !== repoFullName);
      } else {
        return [...prev, repoFullName];
      }
    });
  };

  const loadGitlabRepos = async () => {
    if (hasLoadedGitlabRepos) return;
    
    setLoadingGitlabRepos(true);
    try {
      const data = await api.get('/gitlab/repos');
      console.log('📦 GitLab repos loaded:', data.repos?.length || 0, 'repos');
      setGitlabRepos(data.repos || []);
      setHasLoadedGitlabRepos(true);
      
      if (data.needsReconnect) {
        setToast({
          show: true,
          message: data.message || 'GitLab token expired. Please reconnect GitLab.',
          color: 'warning',
          duration: 8000
        });
      } else if (data.message) {
        setToast({
          show: true,
          message: data.message,
          color: 'warning'
        });
      }
    } catch (error) {
      console.error('Error loading GitLab repos:', error);
      setToast({
        show: true,
        message: 'Failed to load GitLab repositories. Please try again.',
        color: 'danger'
      });
    } finally {
      setLoadingGitlabRepos(false);
    }
  };

  // A selected entry matches a list repo by full_name AND (when present)
  // integrationId — so the same path on a cloud vs a self-hosted instance are
  // distinct. Legacy selections (no integrationId stored) match by name only.
  const gitlabRepoMatches = (selected, repo) => {
    const selName = typeof selected === 'string' ? selected : selected.full_name;
    if (selName !== repo.full_name) return false;
    return !selected.integrationId || selected.integrationId === repo.integrationId;
  };

  const handleGitlabRepoToggle = (repo) => {
    setSelectedGitlabRepos(prev => {
      if (prev.some(r => gitlabRepoMatches(r, repo))) {
        return prev.filter(r => !gitlabRepoMatches(r, repo));
      }
      return [...prev, {
        full_name: repo.full_name,
        default_branch: repo.default_branch || 'main',
        // Pin which connected instance this repo belongs to so reviews/MRs route
        // to the right GitLab and a same-named repo on the other instance stays
        // separate.
        integrationId: repo.integrationId,
        instanceHost: repo.instanceHost,
      }];
    });
  };

  const persistSettings = async (overrides = {}) => {
    setSaving(true);
    try {
      const { analysisGraphConfig: _graphOverride, ...restOverrides } = overrides;

      const updatePayload = {
        githubRepos: selectedRepos,
        gitlabRepos: selectedGitlabRepos,
        aiAgent,
        aiModel,
        ...restOverrides
      };

      const secretsToUpdate = {};
      if (typeof customApiKey === 'string' && !customApiKey.includes('...')) {
        secretsToUpdate.aiApiKey = customApiKey.trim() || null;
      }
      if (typeof customOAuthToken === 'string' && !customOAuthToken.includes('...')) {
        secretsToUpdate.aiOAuthToken = customOAuthToken.trim() || null;
      }
      Object.entries(secrets).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          secretsToUpdate[key] = value;
        }
      });
      if (Object.keys(secretsToUpdate).length > 0) {
        updatePayload.secrets = secretsToUpdate;
      }

      await api.put(`/projects/${projectId}`, updatePayload);
      setToast({
        show: true,
        message: 'Settings saved successfully!',
        color: 'success'
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      setToast({
        show: true,
        message: 'Failed to save settings',
        color: 'danger'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persistSettings();

  if (!project) {
    return (
      <div className="settings-loading">
        <IonSpinner />
        <p>Loading project settings...</p>
      </div>
    );
  }

  return (
    <div className="project-settings-page">

      <div className="settings-content">
        {/* GitHub Integration */}
        <CollapsibleCard
          title="GitHub Repository"
          subtitle="Select GitHub repositories for this project. AI agents will create pull requests here."
          icon={logoGithub}
          defaultExpanded={false}
          lazyLoad={true}
          showHeaderActionsOnlyWhenExpanded={true}
          onToggle={(isExpanded) => {
            if (isExpanded && !hasLoadedRepos) {
              loadGithubRepos();
            }
          }}
          headerActions={
            <IonButton
              fill="clear"
              size="small"
              className="btn-white"
              onClick={handleSave}
              disabled={saving || loadingRepos}
            >
              Save
            </IonButton>
          }
        >
          {loadingRepos ? (
            <div className="repos-loading">
              <IonSpinner />
              <p>Loading repositories...</p>
            </div>
          ) : (
            <div className="repos-list">
              {githubRepos.length === 0 ? (
                <p className="no-repos">No repositories available. Please connect GitHub in Integrations.</p>
              ) : (
                githubRepos.map((repo) => (
                  <IonItem key={repo.full_name} className="repo-item">
                    <IonCheckbox
                      checked={selectedRepos.includes(repo.full_name)}
                      onIonChange={() => handleRepoToggle(repo.full_name)}
                      slot="start"
                    />
                    <IonLabel>
                      <h3>{repo.full_name}</h3>
                    </IonLabel>
                    {repo.private && <span className="repo-badge" slot="end">Private</span>}
                  </IonItem>
                ))
              )}
            </div>
          )}
        </CollapsibleCard>

        {/* GitLab Integration */}
        <CollapsibleCard
          title="GitLab Repository"
          subtitle="Select GitLab repositories for this project. AI agents will create merge requests here."
          icon={gitlabLogoSvg}
          defaultExpanded={false}
          lazyLoad={true}
          showHeaderActionsOnlyWhenExpanded={true}
          onToggle={(isExpanded) => {
            if (isExpanded && !hasLoadedGitlabRepos) {
              loadGitlabRepos();
            }
          }}
          headerActions={
            <IonButton
              fill="clear"
              size="small"
              className="btn-white"
              onClick={handleSave}
              disabled={saving || loadingGitlabRepos}
            >
              Save
            </IonButton>
          }
        >
          {loadingGitlabRepos ? (
            <div className="repos-loading">
              <IonSpinner />
              <p>Loading repositories...</p>
            </div>
          ) : (
            <div className="repos-list">
              {gitlabRepos.length === 0 ? (
                <p className="no-repos">No repositories available. Please connect GitLab in Integrations.</p>
              ) : (
                gitlabRepos.map((repo) => (
                  <IonItem key={`${repo.integrationId || ''}:${repo.full_name}`} className="repo-item">
                    <IonCheckbox
                      checked={selectedGitlabRepos.some(r => gitlabRepoMatches(r, repo))}
                      onIonChange={() => handleGitlabRepoToggle(repo)}
                      slot="start"
                    />
                    <IonLabel>
                      <h3>{repo.full_name}</h3>
                      {repo.instanceHost && <p className="repo-instance">{repo.instanceHost}</p>}
                    </IonLabel>
                    {repo.private && <span className="repo-badge" slot="end">Private</span>}
                  </IonItem>
                ))
              )}
            </div>
          )}
        </CollapsibleCard>

        {/* AI Settings */}
        <CollapsibleCard
          title="AI Settings"
          subtitle="Configure the AI agent and model used for code generation and test automation."
          icon={sparklesOutline}
          defaultExpanded={expandSection === 'ai'}
          showHeaderActionsOnlyWhenExpanded={true}
          headerActions={
            <IonButton
              fill="clear"
              size="small"
              className="btn-white"
              onClick={handleSave}
              disabled={saving}
            >
              Save
            </IonButton>
          }
        >
          <div className="ai-settings-form">
            <div className="ai-setting-group">
              <label className="ai-setting-label">AI Agent</label>
              <select
                value={aiAgent}
                onChange={(e) => {
                  const newAgent = e.target.value;
                  setAiAgent(newAgent);
                  setAiModel(getDefaultModelForAgent(newAgent));
                }}
                className="ai-native-select"
              >
                <option value="cursor">Cursor Agent</option>
                <option value="claude">Claude Code</option>
                <option value="codex">Codex (OpenAI)</option>
                <option value="gemini">Gemini (Google)</option>
              </select>
            </div>

            <div className="ai-setting-group">
              <label className="ai-setting-label">AI Model</label>
              <select
                value={aiModel}
                onChange={(e) => setAiModel(e.target.value)}
                className="ai-native-select"
              >
                {(aiAgent === 'claude' ? CLAUDE_MODELS :
                  aiAgent === 'codex'  ? CODEX_MODELS  :
                  aiAgent === 'gemini' ? GEMINI_MODELS :
                  CURSOR_MODELS).map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Credential fields differ per agent.
                Claude Code uniquely supports TWO credential shapes:
                  - API Key (sk-ant-api03-…)  → ANTHROPIC_API_KEY
                  - Pro/Max OAuth (sk-ant-oat01-…) → CLAUDE_CODE_OAUTH_TOKEN
                User fills either or both; runtime picks OAuth first
                when both present (covered by subscription = $0 incremental).
                Cursor/Codex/Gemini each take a single API key.
            */}
            {aiAgent === 'claude' ? (
              <>
                <div className="ai-setting-group">
                  <label className="ai-setting-label">Anthropic API Key</label>
                  <input
                    type="text"
                    value={customApiKey}
                    onChange={(e) => setCustomApiKey(e.target.value)}
                    onFocus={() => {
                      if (customApiKey && typeof customApiKey === 'string' && customApiKey.includes('...')) {
                        setCustomApiKey('');
                      }
                    }}
                    placeholder="sk-ant-api03-…"
                    className="ai-native-input"
                  />
                  <p className="input-hint">
                    {customApiKey && typeof customApiKey === 'string' && customApiKey.includes('...') && (
                      <span style={{color: 'var(--ion-color-success)', fontWeight: 600}}>✓ Saved. </span>
                    )}
                    Pay-per-token via Anthropic API. From{' '}
                    <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer">console.anthropic.com</a>.
                  </p>
                </div>

                <div className="ai-setting-group">
                  <label className="ai-setting-label">Pro/Max OAuth Token <span style={{color: 'var(--ion-color-success)', fontWeight: 500}}>(preferred — covered by your plan)</span></label>
                  <input
                    type="text"
                    value={customOAuthToken}
                    onChange={(e) => setCustomOAuthToken(e.target.value)}
                    onFocus={() => {
                      if (customOAuthToken && typeof customOAuthToken === 'string' && customOAuthToken.includes('...')) {
                        setCustomOAuthToken('');
                      }
                    }}
                    placeholder="sk-ant-oat01-…"
                    className="ai-native-input"
                  />
                  <p className="input-hint">
                    {customOAuthToken && typeof customOAuthToken === 'string' && customOAuthToken.includes('...') && (
                      <span style={{color: 'var(--ion-color-success)', fontWeight: 600}}>✓ Saved. </span>
                    )}
                    OAuth token from your Claude Pro / Max subscription. Run{' '}
                    <code>claude login</code> locally then grab it from <code>~/.claude.json</code>{' '}
                    (no per-token cost on top of your plan).
                    {customOAuthToken && customApiKey && !customApiKey.includes('...') && !customOAuthToken.includes('...') && (
                      <> Both filled — runtime uses OAuth.</>
                    )}
                  </p>
                </div>
              </>
            ) : (
              <div className="ai-setting-group">
                <label className="ai-setting-label">API Key (Required)</label>
                <input
                  type="text"
                  value={customApiKey}
                  onChange={(e) => setCustomApiKey(e.target.value)}
                  onFocus={() => {
                    if (customApiKey && typeof customApiKey === 'string' && customApiKey.includes('...')) {
                      setCustomApiKey('');
                    }
                  }}
                  placeholder={
                    aiAgent === 'codex'  ? 'sk-…  (OpenAI API key)' :
                    aiAgent === 'gemini' ? 'AIza… (Google API key)' :
                    'key_…  (Cursor API key)'
                  }
                  className="ai-native-input"
                />
                <p className="input-hint">
                  {customApiKey && typeof customApiKey === 'string' && customApiKey.includes('...') && (
                    <span style={{color: 'var(--ion-color-success)', fontWeight: 600}}>✓ Saved. </span>
                  )}
                  {aiAgent === 'codex' && (
                    <>OpenAI API key (<code>sk-…</code>). From{' '}
                    <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com</a>.</>
                  )}
                  {aiAgent === 'gemini' && (
                    <>Google AI API key (<code>AIza…</code>). From{' '}
                    <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer">aistudio.google.com</a>.</>
                  )}
                  {aiAgent === 'cursor' && (
                    <>Cursor API key (<code>key_…</code>). From{' '}
                    <a href="https://cursor.com/settings" target="_blank" rel="noopener noreferrer">cursor.com/settings</a>.</>
                  )}
                  {' '}Required. Encrypted and stored securely.
                </p>
              </div>
            )}
          </div>
        </CollapsibleCard>

      </div>
    </div>
  );
};

export default ProjectSettings;
